import { useState } from 'react';
import { Box, Dialog, IconButton, Skeleton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import type { PlanimetryDescriptor } from '@/types';

interface PlanimetryProps {
  descriptor: PlanimetryDescriptor | null;
  isLoading: boolean;
}

/**
 * Viewer della planimetria: mostra l'immagine composta all'edge (Worker Cloudflare) via
 * `composeUrl`. Thin client: nessuna logica di layer qui — l'ordine/selezione dei layer è
 * deciso dal backend. Click sull'immagine → zoom a tutto schermo ("planimetria quotata").
 */
export function Planimetry({ descriptor, isLoading }: PlanimetryProps): JSX.Element {
  const [zoomOpen, setZoomOpen] = useState(false);
  const src = descriptor?.composeUrl ?? null;

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', letterSpacing: '0.08em', fontWeight: 600 }}
      >
        Il tuo appartamento
      </Typography>

      {isLoading && !src ? (
        <Skeleton variant="rectangular" sx={{ width: '100%', pt: '75%', borderRadius: 1, mt: 1 }} />
      ) : src ? (
        <Box
          role="button"
          tabIndex={0}
          aria-label="Ingrandisci la planimetria"
          onClick={() => setZoomOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setZoomOpen(true);
          }}
          sx={{
            position: 'relative',
            mt: 1,
            cursor: 'zoom-in',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: 'background.paper',
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
          }}
        >
          <Box
            component="img"
            src={src}
            alt="Planimetria della configurazione"
            sx={{ display: 'block', width: '100%', height: 'auto' }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.55)',
              color: '#fff',
              borderRadius: '50%',
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ZoomInIcon fontSize="small" />
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Planimetria non disponibile per questa configurazione.
        </Typography>
      )}

      <Dialog open={zoomOpen} onClose={() => setZoomOpen(false)} maxWidth="lg" fullWidth>
        <Box sx={{ position: 'relative', bgcolor: '#fff' }}>
          <IconButton
            aria-label="Chiudi"
            onClick={() => setZoomOpen(false)}
            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.06)' }}
          >
            <CloseIcon />
          </IconButton>
          {src && (
            <Box
              component="img"
              src={src}
              alt="Planimetria della configurazione (ingrandita)"
              sx={{ display: 'block', width: '100%', height: 'auto' }}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
